import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Send, Loader2, MessagesSquare, Plus, Trash2, AlertTriangle, Crown } from "lucide-react";
import { toast } from "sonner";
import VoiceButton from "@/components/VoiceButton";
import { useProfile } from "@/context/ProfileContext";
import { usePlan } from "@/context/PlanContext";

const SUGGESTIONS = [
  "Which of my meds shouldn't be taken with alcohol?",
  "Are there any risky combos in my regimen?",
  "What symptoms should I watch for on my current stack?",
  "Can I take my supplements at the same time as my prescriptions?",
];

export default function Chat() {
  const navigate = useNavigate();
  const { sessionId: routeSessionId } = useParams();
  const { activeId } = useProfile();
  const { isPro } = usePlan();
  const [sessionId, setSessionId] = useState(routeSessionId || null);
  const [sessions, setSessions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [pendingAnswer, setPendingAnswer] = useState("");
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  const loadSessions = async () => {
    try {
      const { data } = await api.get("/chat/sessions");
      setSessions(data);
    } catch (_e) {}
  };
  const loadSession = async (id) => {
    if (!id) {
      setMessages([]);
      return;
    }
    try {
      const { data } = await api.get(`/chat/sessions/${id}`);
      setMessages(data);
    } catch (_e) {
      setMessages([]);
    }
  };

  useEffect(() => {
    if (!activeId) return;
    loadSessions();
  }, [activeId]);

  useEffect(() => {
    setSessionId(routeSessionId || null);
    loadSession(routeSessionId);
  }, [routeSessionId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, pendingAnswer]);

  const send = async (text) => {
    const clean = (text || input).trim();
    if (!clean || streaming) return;
    setInput("");
    setPendingAnswer("");
    setStreaming(true);
    const nowIso = new Date().toISOString();
    setMessages((m) => [...m, { role: "user", content: clean, created_at: nowIso }]);

    const controller = new AbortController();
    abortRef.current = controller;
    let localSessionId = sessionId;
    let accumulated = "";

    try {
      const token = localStorage.getItem("harmonix_token") || "";
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        credentials: "include",
        body: JSON.stringify({ text: clean, session_id: localSessionId }),
        signal: controller.signal,
      });
      if (!res.ok || !res.body) {
        let payload = {};
        try {
          payload = await res.clone().json();
        } catch (_e) {}
        if (res.status === 402 && payload?.detail?.code === "upgrade_required") {
          toast.info("Ask Harmonix is a Pro feature. Redirecting…");
          setTimeout(() => navigate("/pricing"), 800);
          setStreaming(false);
          return;
        }
        throw new Error("Stream failed");
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split("\n\n");
        buffer = frames.pop() || "";
        for (const f of frames) {
          const lines = f.split("\n");
          const event = (lines.find((l) => l.startsWith("event:")) || "").slice(6).trim();
          const dataLine = (lines.find((l) => l.startsWith("data:")) || "").slice(5).trim();
          if (!dataLine) continue;
          let payload = {};
          try {
            payload = JSON.parse(dataLine);
          } catch (_e) {}
          if (event === "meta" && payload.session_id) {
            localSessionId = payload.session_id;
            setSessionId(localSessionId);
            if (!routeSessionId) {
              window.history.replaceState(null, "", `/chat/${localSessionId}`);
            }
          } else if (event === "delta" && payload.text) {
            accumulated += payload.text;
            setPendingAnswer(accumulated);
          } else if (event === "error") {
            toast.error(payload.message || "Chat error");
          }
        }
      }

      if (accumulated) {
        setMessages((m) => [
          ...m,
          { role: "assistant", content: accumulated, created_at: new Date().toISOString() },
        ]);
      }
      setPendingAnswer("");
      loadSessions();
    } catch (e) {
      if (e.name !== "AbortError") {
        toast.error("Chat failed. Please try again.");
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const startNew = () => {
    setSessionId(null);
    setMessages([]);
    setPendingAnswer("");
    navigate("/chat");
  };

  const deleteSession = async (id) => {
    try {
      await api.delete(`/chat/sessions/${id}`);
      setSessions((s) => s.filter((x) => x.session_id !== id));
      if (id === sessionId) startNew();
    } catch (_e) {}
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10 fade-up">
      {!isPro && (
        <div
          className="card-warm p-5 mb-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between"
          style={{ background: "linear-gradient(135deg,#2D5A4C,#23473C)", color: "#FAF7F2", borderColor: "#23473C" }}
          data-testid="chat-upgrade-banner"
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0" style={{ background: "#D96B43" }}>
              <Crown size={18} color="white" />
            </div>
            <div>
              <p className="font-serif-display text-xl font-medium">Ask Harmonix is a Pro feature</p>
              <p className="text-sm" style={{ color: "#E8F0EC" }}>
                Get unlimited chat, voice, insights, and family profiles for $9/mo.
              </p>
            </div>
          </div>
          <Button
            onClick={() => navigate("/pricing")}
            className="btn-terracotta rounded-full h-11 px-5 shrink-0"
            data-testid="chat-upgrade-btn"
          >
            Upgrade to Pro
          </Button>
        </div>
      )}
      <div className="grid lg:grid-cols-4 gap-4 h-[calc(100vh-220px)] min-h-[560px]">
        {/* Sessions sidebar */}
        <aside className="lg:col-span-1 card-warm p-4 flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-serif-display text-xl font-medium" style={{ color: "#1C2826" }}>
              Chats
            </h2>
            <Button
              data-testid="new-chat-btn"
              size="sm"
              onClick={startNew}
              className="btn-brand rounded-full h-9"
            >
              <Plus size={14} className="mr-1" /> New
            </Button>
          </div>
          <div className="flex-1 overflow-auto space-y-1.5" data-testid="chat-sessions-list">
            {sessions.length === 0 ? (
              <p className="text-xs" style={{ color: "#5C6B66" }}>
                Your saved chats will appear here.
              </p>
            ) : (
              sessions.map((s) => (
                <div
                  key={s.session_id}
                  className="group flex items-center gap-1 rounded-xl border"
                  style={{
                    background: s.session_id === sessionId ? "#E8F0EC" : "#FAF7F2",
                    borderColor: "#E5DEC9",
                  }}
                >
                  <button
                    data-testid={`open-chat-session-${s.session_id}`}
                    onClick={() => navigate(`/chat/${s.session_id}`)}
                    className="flex-1 text-left p-2.5 text-xs"
                    style={{ color: "#1C2826" }}
                  >
                    <p className="line-clamp-2">{s.preview || "New chat"}</p>
                    <p className="mt-1 text-[10px]" style={{ color: "#5C6B66" }}>
                      {new Date(s.last_at).toLocaleDateString()} · {s.count} msgs
                    </p>
                  </button>
                  <button
                    onClick={() => deleteSession(s.session_id)}
                    aria-label="Delete chat"
                    className="p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 size={14} style={{ color: "#BF5832" }} />
                  </button>
                </div>
              ))
            )}
          </div>
        </aside>

        {/* Chat area */}
        <section className="lg:col-span-3 card-warm p-0 flex flex-col overflow-hidden">
          <header
            className="px-6 py-4 flex items-center gap-2 border-b"
            style={{ borderColor: "#E5DEC9", background: "#F4EFE6" }}
          >
            <div className="w-9 h-9 rounded-xl grid place-items-center" style={{ background: "#2D5A4C" }}>
              <MessagesSquare size={18} color="white" />
            </div>
            <div>
              <h1 className="font-serif-display text-2xl font-medium" style={{ color: "#1C2826" }}>
                Ask Harmonix
              </h1>
              <p className="text-xs" style={{ color: "#5C6B66" }}>
                Powered by GPT 5.6 Terra · Your regimen is auto-attached to every question
              </p>
            </div>
          </header>

          <div
            ref={scrollRef}
            className="flex-1 overflow-auto px-4 sm:px-6 py-5"
            data-testid="chat-messages-scroll"
          >
            {messages.length === 0 && !pendingAnswer ? (
              <div className="max-w-2xl mx-auto mt-8">
                <div
                  className="text-sm rounded-xl p-4 flex gap-3 items-start mb-6"
                  style={{ background: "#FDF0EA", border: "1px solid #E5DEC9", color: "#5C3B2A" }}
                >
                  <AlertTriangle size={16} className="mt-0.5 shrink-0" style={{ color: "#BF5832" }} />
                  <span>
                    Ask Harmonix is informational. For emergencies, dosing changes, or
                    personal medical advice, please talk to your doctor or pharmacist.
                  </span>
                </div>
                <p className="text-sm font-semibold mb-3" style={{ color: "#1C2826" }}>
                  Try one of these to get started:
                </p>
                <div className="grid sm:grid-cols-2 gap-2">
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={i}
                      data-testid={`chat-suggestion-${i}`}
                      onClick={() => send(s)}
                      className="text-left text-sm p-3 rounded-xl border transition-transform hover:-translate-y-0.5"
                      style={{ borderColor: "#E5DEC9", background: "#FFFFFF", color: "#1C2826" }}
                    >
                      <Sparkles size={14} className="inline mr-2" style={{ color: "#D96B43" }} />
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-4" data-testid="chat-messages-list">
                {messages.map((m, i) => (
                  <Bubble key={i} role={m.role} content={m.content} />
                ))}
                {pendingAnswer && <Bubble role="assistant" content={pendingAnswer} streaming />}
              </div>
            )}
          </div>

          <footer
            className="p-4 border-t"
            style={{ borderColor: "#E5DEC9", background: "#FFFFFF" }}
          >
            <div className="flex gap-2 items-end max-w-4xl mx-auto">
              <VoiceButton
                sessionId={sessionId}
                disabled={streaming}
                onExchange={(data) => {
                  if (data.session_id && data.session_id !== sessionId) {
                    setSessionId(data.session_id);
                    window.history.replaceState(null, "", `/chat/${data.session_id}`);
                  }
                  const nowIso = new Date().toISOString();
                  setMessages((m) => [
                    ...m,
                    { role: "user", content: data.user_text, created_at: nowIso },
                    { role: "assistant", content: data.assistant_text, created_at: nowIso },
                  ]);
                  loadSessions();
                }}
              />
              <Textarea
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder="Ask about your meds, symptoms, or interactions…"
                className="rounded-2xl resize-none min-h-[52px] max-h-40"
                rows={1}
              />
              <Button
                data-testid="chat-send-btn"
                onClick={() => send()}
                disabled={streaming || !input.trim()}
                className="btn-brand rounded-full h-12 w-12 p-0 shrink-0"
                aria-label="Send"
              >
                {streaming ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </Button>
            </div>
            <p className="mt-2 text-[11px] text-center" style={{ color: "#5C6B66" }}>
              Tap the mic to talk hands-free. Harmonix can make mistakes — always verify important health decisions with a clinician.
            </p>
          </footer>
        </section>
      </div>
    </div>
  );
}

function Bubble({ role, content, streaming }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className="max-w-[85%] sm:max-w-[75%] px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap"
        style={
          isUser
            ? { background: "#2D5A4C", color: "#FAF7F2", borderBottomRightRadius: 6 }
            : { background: "#F4EFE6", color: "#1C2826", borderBottomLeftRadius: 6, border: "1px solid #E5DEC9" }
        }
      >
        {content}
        {streaming && <span className="inline-block ml-1 pulse-dot">▍</span>}
      </div>
    </div>
  );
}
